import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Archive, ArchiveRestore, Lock, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { EmptyState, Panel, PanelHeader, SkeletonTable } from '@/components/app/States';
import { Note } from '@/components/app/Badges';
import { Field, FormSheet, HistoryList, RefSelect, SelectField, SheetSection, StatusPill, inputClass } from '@/components/app/DriftUI';
import { adminAddSubproject, adminCreateItem, adminDeleteItem, adminListRegister, adminPatchItem, adminPatchSubproject } from '@/lib/driftApi';
import { cn } from '@/lib/utils';

/*
  Generisk registerliste + redigeringspanel for Innstillinger. Feltene styres av backendens feltspesifikasjon,
  så validering og etiketter er like i skjema og API. Arkivering (aktiv/inaktiv) fremfor sletting – sletting kun for ubrukte poster.
  Alle endringer lagres før → etter i postens historikk («Kontor (eksempeldata)»).
*/

/** Hvilken referanseliste et ref-felt henter valg fra. */
const REF_LIST_KEY = { employees: 'employees', customers: 'customers', departments: 'departments', groups: 'groups', trades: 'trades', itemCategories: 'categories', competenceTypes: 'competenceTypes' };

/** Felt som vises i radens metalinje (kort, det viktigste først). */
const ROW_META = {
  employees: ['role', 'departmentId', 'demoRole'],
  customers: ['location'],
  projects: ['customerId', 'location', 'responsibleId'],
  activities: ['type'],
  wageTypes: ['type', 'requiresComment'],
  supplements: ['unit', 'requiresComment'],
  items: ['category', 'unit'],
};

const FILTERS = [
  { id: 'aktive', label: 'Aktive' },
  { id: 'arkiverte', label: 'Arkiverte' },
  { id: 'alle', label: 'Alle' },
];

export const isArchived = (item) => item.active === false;
export const isClosed = (item) => item.status === 'avsluttet';
const isInactive = (item) => isArchived(item) || isClosed(item);

export function refLabel(refs, refName, id) {
  if (!id) return null;
  const list = refs?.[REF_LIST_KEY[refName] ?? refName] ?? [];
  const x = list.find((r) => r.id === id);
  return x ? x.name ?? x.label ?? id : id;
}

export function formatFieldValue(spec, value, refs) {
  if (value === null || value === undefined || value === '') return null;
  if (!spec) return String(value);
  if (spec.kind === 'ref') return refLabel(refs, spec.ref, value);
  if (spec.kind === 'select') return spec.optionLabels?.[value] ?? value;
  if (spec.kind === 'bool') return value ? 'Ja' : 'Nei';
  if (spec.kind === 'competences') return value.length ? `${value.length} ${value.length === 1 ? 'kompetanse' : 'kompetanser'}` : null;
  return String(value);
}

/** Søk på tvers av alle tekstverdier i posten (id, navn, kode, etiketter). */
export function matchesSearch(item, q, fields, refs) {
  if (!q) return true;
  const needle = q.trim().toLowerCase();
  const hay = [item.id, item.code, item.name, item.label, item.customer, item.location, item.role, item.email];
  for (const [key, spec] of Object.entries(fields ?? {})) {
    if (spec.kind === 'ref' || spec.kind === 'select') hay.push(formatFieldValue(spec, item[key], refs));
  }
  (item.subprojects ?? []).forEach((s) => hay.push(s.name, s.id));
  return hay.filter(Boolean).some((v) => String(v).toLowerCase().includes(needle));
}

const emptyForm = (fields, item) => {
  const f = { reason: '' };
  for (const [key, spec] of Object.entries(fields)) {
    if (spec.kind === 'bool') f[key] = item ? !!item[key] : false;
    else if (spec.kind === 'competences') f[key] = (item?.[key] ?? []).map((c) => ({ ...c, validTo: c.validTo ?? '', note: c.note ?? '' }));
    else f[key] = item?.[key] ?? '';
  }
  f.newId = '';
  f.subprojectsText = '';
  return f;
};

function validate(fields, form) {
  const errors = {};
  for (const [key, spec] of Object.entries(fields)) {
    if (key === 'status') continue;
    if (spec.required && (form[key] === '' || form[key] === null || form[key] === undefined)) errors[key] = `${spec.label} må fylles ut`;
    if (spec.kind === 'email' && form[key] && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form[key])) errors[key] = 'E-postadressen ser ikke gyldig ut';
    if (spec.kind === 'competences') form[key].forEach((c) => { if (!c.typeId) errors[key] = 'Velg kompetansetype på hver linje'; });
  }
  return errors;
}

function toBody(fields, form, { create, item }) {
  const body = {};
  for (const [key, spec] of Object.entries(fields)) {
    if (key === 'status' && create) continue;
    if (spec.kind === 'competences') body[key] = form[key].map((c) => ({ typeId: c.typeId, validTo: c.validTo || null, documented: !!c.documented, note: c.note || null }));
    else if (spec.kind === 'bool') body[key] = !!form[key];
    else body[key] = form[key] === '' ? null : form[key];
    // Uendret verdi sendes ikke ved redigering (så arkiverte referanser som alt står på posten beholdes)
    if (!create && item && JSON.stringify(body[key] ?? null) === JSON.stringify(item[key] ?? null)) delete body[key];
  }
  if (create && form.newId?.trim()) body.id = form.newId.trim();
  if (create && form.subprojectsText?.trim()) body.subprojects = form.subprojectsText.split('\n').map((s) => s.trim()).filter(Boolean).map((name) => ({ name }));
  if (form.reason?.trim()) body.reason = form.reason.trim();
  return body;
}

/* ---------- Kompetanse (eksempler – ingen godkjenning av maskinbruk) ---------- */

function CompetenceEditor({ value, onChange, refs, testId }) {
  const types = refs?.competenceTypes ?? [];
  const add = () => onChange([...value, { typeId: '', validTo: '', documented: false, note: '' }]);
  const set = (i, patch) => onChange(value.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2" data-testid={testId}>
      {value.map((c, i) => (
        <div key={i} className="rounded-[10px] border border-[var(--hr-hairline)] p-2.5" data-testid={`${testId}-row-${i}`}>
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <RefSelect value={c.typeId} onValueChange={(v) => set(i, { typeId: v })} list={types} placeholder="Velg kompetansetype" testId={`${testId}-type-${i}`} />
            <Button type="button" variant="ghost" size="icon" className="h-10 w-10 rounded-[8px]" onClick={() => remove(i)} aria-label="Fjern kompetanse" data-testid={`${testId}-remove-${i}`}>
              <Trash2 className="h-4 w-4 text-[var(--hr-muted)]" aria-hidden="true" />
            </Button>
          </div>
          <div className="mt-2 grid grid-cols-2 items-center gap-2">
            <Input type="date" value={c.validTo} onChange={(e) => set(i, { validTo: e.target.value })} aria-label="Gyldig til" className={cn(inputClass, 'h-10 text-[14px]')} data-testid={`${testId}-validto-${i}`} />
            <label className="flex h-10 items-center gap-2 text-[13px] text-[var(--hr-text)]">
              <Switch checked={!!c.documented} onCheckedChange={(v) => set(i, { documented: v })} data-testid={`${testId}-documented-${i}`} />
              Dokumentert
            </label>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" className="h-8 rounded-[8px] bg-white text-[13px]" onClick={add} data-testid={`${testId}-add`}>
        <Plus className="mr-1 h-3.5 w-3.5" aria-hidden="true" /> Legg til kompetanse
      </Button>
      <p className="text-[13px] leading-5 text-[var(--hr-muted-2)]">Eksempler på kompetansetyper med gyldighet og «dokumentert». Dette er ikke en godkjenning av maskinbruk og erstatter ikke reelle bevis.</p>
    </div>
  );
}

/* ---------- Underprosjekter (redigering av eksisterende prosjekt) ---------- */

function SubprojectEditor({ project, onChanged, testId }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const subs = project.subprojects ?? [];
  const add = async () => {
    if (name.trim().length < 2) return;
    setBusy(true);
    try {
      onChanged(await adminAddSubproject(project.id, { name: name.trim() }));
      setName('');
      toast.success('Underprosjekt lagt til');
    } catch (err) {
      toast.error('Kunne ikke legge til underprosjekt', { description: err.message });
    } finally {
      setBusy(false);
    }
  };
  const toggle = async (s) => {
    setBusy(true);
    try {
      onChanged(await adminPatchSubproject(project.id, s.id, { active: s.active === false }));
    } catch (err) {
      toast.error('Kunne ikke endre underprosjektet', { description: err.message });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2" data-testid={testId}>
      {subs.length === 0 && <p className="text-[13px] text-[var(--hr-muted)]">Ingen underprosjekter. Registreringer føres da på hele prosjektet.</p>}
      {subs.map((s) => (
        <div key={s.id} className="flex items-center justify-between gap-2 rounded-[8px] border border-[var(--hr-hairline)] px-2.5 py-1.5 text-[14px]" data-testid={`${testId}-row-${s.id}`}>
          <span className="min-w-0 truncate">
            <span className="hr-tabular text-[var(--hr-muted)]">{s.id}</span> <span className={cn(s.active === false && 'text-[var(--hr-muted)] line-through')}>{s.name}</span>
          </span>
          <button type="button" onClick={() => toggle(s)} disabled={busy} className="shrink-0 rounded-sm text-[13px] font-medium text-[var(--hr-muted)] underline-offset-2 hover:text-[var(--hr-heading)] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)] disabled:opacity-50" data-testid={`${testId}-toggle-${s.id}`}>
            {s.active === false ? 'Reaktiver' : 'Arkiver'}
          </button>
        </div>
      ))}
      <div className="flex gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="Nytt underprosjekt" aria-label="Nytt underprosjekt" className={cn(inputClass, 'h-10 text-[14px]')} data-testid={`${testId}-new-name`} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())} />
        <Button type="button" variant="outline" className="h-10 rounded-[8px] bg-white" onClick={add} disabled={busy || name.trim().length < 2} data-testid={`${testId}-add`}>
          Legg til
        </Button>
      </div>
    </div>
  );
}

/* ---------- Generisk skjema ---------- */

function FieldInput({ name, fieldKey, spec, form, setForm, errors, refs, item, testId }) {
  const set = (v) => setForm((f) => ({ ...f, [fieldKey]: v }));
  const id = `${name}-${fieldKey}`;
  const value = form[fieldKey];
  const lockedUnit = name === 'items' && fieldKey === 'unit' && item?.unitLocked;
  if (fieldKey === 'status') return null; // avslutt/gjenåpne gjøres med egen knapp
  if (spec.kind === 'competences') {
    return (
      <SheetSection title="Kompetanse" description="Valgfritt. Eksempeldata – innebærer ingen godkjenning av maskinbruk.">
        <CompetenceEditor value={value} onChange={set} refs={refs} testId={`${testId}-competences`} />
        {errors[fieldKey] && <p className="mt-1 text-[13px] text-[var(--hr-red)]" role="alert">{errors[fieldKey]}</p>}
      </SheetSection>
    );
  }
  const hint = fieldKey === 'demoRole' ? 'Kun visning i demoen – endrer ikke pålogging eller ekte tilganger.' : fieldKey === 'email' ? 'Valgfri eksempeladresse – ingen e-post sendes.' : lockedUnit ? `Låst: varen har ${item.usage.total} varelinjer. Arkiver varen og opprett en ny ved annen enhet.` : fieldKey === 'requiresComment' ? 'Kommentar kreves ved føring. Satser er ikke konfigurert.' : undefined;
  return (
    <Field label={spec.label} htmlFor={id} required={spec.required} error={errors[fieldKey]} hint={hint}>
      {spec.kind === 'textarea' ? (
        <Textarea id={id} value={value} onChange={(e) => set(e.target.value)} rows={2} maxLength={spec.max} className="min-h-[64px] rounded-[10px] border-[var(--hr-border)] text-[16px] shadow-none lg:text-[14px]" data-testid={`${testId}-${fieldKey}`} />
      ) : spec.kind === 'select' ? (
        lockedUnit ? (
          <div className={cn(inputClass, 'flex items-center gap-2 bg-[var(--hr-surface-2)] px-3 text-[var(--hr-muted)]')} data-testid={`${testId}-${fieldKey}-locked`}>
            <Lock className="h-3.5 w-3.5" aria-hidden="true" /> {spec.optionLabels?.[value] ?? value}
          </div>
        ) : (
          <SelectField id={id} value={value} onValueChange={set} allowEmpty={!spec.required} emptyLabel="Ikke valgt" options={spec.options.map((o) => ({ value: o, label: spec.optionLabels?.[o] ?? o }))} testId={`${testId}-${fieldKey}`} />
        )
      ) : spec.kind === 'ref' ? (
        <RefSelect id={id} value={value} onValueChange={set} allowEmpty={!spec.required} emptyLabel="Ikke valgt" list={refs?.[REF_LIST_KEY[spec.ref] ?? spec.ref]} testId={`${testId}-${fieldKey}`} />
      ) : spec.kind === 'bool' ? (
        <label className="flex h-10 items-center gap-2 text-[14px] text-[var(--hr-text)]">
          <Switch id={id} checked={!!value} onCheckedChange={set} data-testid={`${testId}-${fieldKey}`} /> {value ? 'Ja' : 'Nei'}
        </label>
      ) : (
        <Input id={id} type={spec.kind === 'email' ? 'email' : 'text'} value={value} onChange={(e) => set(e.target.value)} maxLength={spec.max} className={inputClass} data-testid={`${testId}-${fieldKey}`} />
      )}
    </Field>
  );
}

/* ---------- Register ---------- */

/**
 * Ett register: søk, aktiv/arkivert-filter, én primærhandling («Ny …»), kompakt liste og redigeringspanel.
 * `search` (fra sidens søkefelt) kombineres med registerets eget filter.
 */
export function AdminRegister({ name, refs, search = '', openId, onLoaded }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const [filter, setFilter] = useState('aktive');
  const [sheet, setSheet] = useState(null); // null | { mode: 'ny' } | { mode: 'rediger', item }
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null); // { kind: 'arkiver' | 'slett', item }
  const [openedId, setOpenedId] = useState(null);
  const testId = `admin-${name}`;

  const load = useCallback(async () => {
    setStatus((s) => (s === 'ready' ? 'refreshing' : 'loading'));
    try {
      const d = await adminListRegister(name);
      setData(d);
      setStatus('ready');
      onLoaded?.(name, d);
    } catch {
      setStatus('error');
    }
  }, [name, onLoaded]);

  useEffect(() => {
    setFilter('aktive');
    setSheet(null);
    load();
  }, [load]);

  const fields = useMemo(() => data?.fields ?? {}, [data]);
  const items = useMemo(() => {
    const all = data?.items ?? [];
    const byFilter = all.filter((x) => (filter === 'alle' ? true : filter === 'arkiverte' ? isInactive(x) : !isInactive(x)));
    return byFilter.filter((x) => matchesSearch(x, search, fields, refs));
  }, [data, filter, search, fields, refs]);
  const counts = useMemo(() => {
    const all = data?.items ?? [];
    return { aktive: all.filter((x) => !isInactive(x)).length, arkiverte: all.filter(isInactive).length, alle: all.length };
  }, [data]);

  const openNew = () => {
    setErrors({});
    setForm(emptyForm(fields, null));
    setSheet({ mode: 'ny' });
  };
  const openEdit = (item) => {
    setErrors({});
    setForm(emptyForm(fields, item));
    setSheet({ mode: 'rediger', item });
  };

  // ?post=<id> (fra søket i oversikten): åpne posten direkte når listen er lastet.
  useEffect(() => {
    if (!openId || !data || openedId === openId) return;
    const item = data.items.find((x) => x.id === openId);
    if (item) {
      setOpenedId(openId);
      if (isInactive(item)) setFilter('alle');
      setErrors({});
      setForm(emptyForm(data.fields, item));
      setSheet({ mode: 'rediger', item });
    }
  }, [openId, data, openedId]);
  const replaceItem = (updated) => {
    setData((d) => (d ? { ...d, items: d.items.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)) } : d));
    setSheet((s) => (s?.mode === 'rediger' ? { mode: 'rediger', item: { ...s.item, ...updated } } : s));
  };

  const save = async () => {
    const e = validate(fields, form);
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      if (sheet.mode === 'ny') {
        const created = await adminCreateItem(name, toBody(fields, form, { create: true }));
        toast.success(`${data.singular} opprettet`, { description: `${created.id} · ${created.name ?? created.label}` });
        setSheet(null);
        load();
      } else {
        const body = toBody(fields, form, { create: false, item: sheet.item });
        if (!Object.keys(body).some((k) => k !== 'reason')) {
          toast.info('Ingen endringer å lagre');
          return;
        }
        const updated = await adminPatchItem(name, sheet.item.id, body);
        replaceItem(updated);
        toast.success('Endringen er lagret', { description: 'Før → etter ligger i historikken.' });
        setSheet(null);
        load();
      }
    } catch (err) {
      toast.error('Kunne ikke lagre', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (item, active) => {
    setSaving(true);
    try {
      const updated = await adminPatchItem(name, item.id, { active, reason: form.reason?.trim() || null });
      replaceItem(updated);
      toast.success(active ? `${data.singular} er reaktivert` : `${data.singular} er arkivert`, { description: active ? 'Kan velges igjen i nye registreringer.' : 'Tilbys ikke i nye valg. Gamle registreringer og rapporter beholder posten.' });
      load();
    } catch (err) {
      toast.error('Kunne ikke endre status', { description: err.message });
    } finally {
      setSaving(false);
      setConfirm(null);
    }
  };

  const setProjectStatus = async (item, statusValue) => {
    setSaving(true);
    try {
      const updated = await adminPatchItem(name, item.id, { status: statusValue, reason: form.reason?.trim() || null });
      replaceItem(updated);
      toast.success(statusValue === 'avsluttet' ? 'Prosjektet er avsluttet' : 'Prosjektet er gjenåpnet', { description: statusValue === 'avsluttet' ? 'Vises fortsatt i historikk og rapporter, men tilbys ikke for nye registreringer.' : 'Kan velges igjen i nye registreringer.' });
      load();
    } catch (err) {
      toast.error('Kunne ikke endre status', { description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    setSaving(true);
    try {
      await adminDeleteItem(name, item.id);
      toast.success(`${data.singular} er slettet`);
      setSheet(null);
      load();
    } catch (err) {
      toast.error('Kunne ikke slette', { description: err.message });
    } finally {
      setSaving(false);
      setConfirm(null);
    }
  };

  const editing = sheet?.mode === 'rediger';
  const current = editing ? data?.items.find((x) => x.id === sheet.item.id) ?? sheet.item : null;
  const nameKey = fields.name ? 'name' : 'label';
  const codeOf = (x) => x.code ?? (data?.idEditable ? x.id : null);
  const metaOf = (x) => (ROW_META[name] ?? []).map((k) => formatFieldValue(fields[k], x[k], refs)).filter(Boolean);
  const usageText = (x) => (x.usage?.total ? `${x.usage.total} ${name === 'projects' || name === 'employees' ? 'registreringer' : name === 'customers' ? 'prosjekter' : name === 'itemCategories' ? 'varer' : name === 'items' ? 'varelinjer' : name === 'departments' || name === 'groups' || name === 'trades' || name === 'competenceTypes' ? 'medarbeidere' : 'føringer'}` : 'Ikke i bruk');

  return (
    <Panel className="min-w-0" data-testid={`${testId}-panel`}>
      <PanelHeader
        title={data?.label ?? '…'}
        description={status === 'ready' || status === 'refreshing' ? `${counts.aktive} aktive · ${counts.arkiverte} ${name === 'projects' ? 'avsluttede/arkiverte' : 'arkiverte'}` : undefined}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-[8px] border border-[var(--hr-border)] bg-white p-0.5" role="tablist" aria-label="Vis" data-testid={`${testId}-filter`}>
              {FILTERS.map((f) => (
                <button key={f.id} type="button" role="tab" aria-selected={filter === f.id} onClick={() => setFilter(f.id)} className={cn('h-7 rounded-[6px] px-2.5 text-[13px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]', filter === f.id ? 'bg-[var(--hr-heading)] text-white' : 'text-[var(--hr-muted)] hover:bg-[var(--hr-surface-2)] hover:text-[var(--hr-heading)]')} data-testid={`${testId}-filter-${f.id}`}>
                  {f.id === 'arkiverte' && name === 'projects' ? 'Avsluttede' : f.label} <span className={cn('hr-tabular', filter === f.id ? 'text-white/70' : 'text-[var(--hr-muted-2)]')}>{counts[f.id]}</span>
                </button>
              ))}
            </div>
            {data && (
              <Button onClick={openNew} className="h-9 rounded-[8px] bg-[var(--hr-heading)] px-3 text-[14px] font-semibold text-white shadow-none hover:bg-[var(--hr-navy)]" data-testid={`${testId}-new-button`}>
                <Plus className="mr-1 h-4 w-4" aria-hidden="true" /> Ny {data.singular.toLowerCase()}
              </Button>
            )}
          </div>
        }
      />

      {status === 'loading' && <div className="p-4"><SkeletonTable rows={5} /></div>}
      {status === 'error' && (
        <div className="p-4">
          <Note tone="amber" role="alert" data-testid={`${testId}-error`}>
            Kunne ikke hente registeret.{' '}
            <Button variant="link" className="h-auto px-1" onClick={load} data-testid={`${testId}-retry`}>Prøv igjen</Button>
          </Note>
        </div>
      )}
      {(status === 'ready' || status === 'refreshing') && items.length === 0 && (
        <div className="p-4">
          <EmptyState title={search ? 'Ingen treff' : filter === 'arkiverte' ? 'Ingen arkiverte' : `Ingen ${data.label.toLowerCase()}`} description={search ? 'Prøv et annet søkeord eller et annet filter.' : filter === 'arkiverte' ? 'Arkiverte poster havner her og kan reaktiveres.' : `Opprett ${data.singular.toLowerCase()} for å komme i gang.`} onReset={filter !== 'alle' ? () => setFilter('alle') : undefined} resetLabel="Vis alle" action={!search ? <Button size="sm" onClick={openNew} data-testid={`${testId}-empty-new`}>Ny {data.singular.toLowerCase()}</Button> : null} className="py-8" />
        </div>
      )}
      {(status === 'ready' || status === 'refreshing') && items.length > 0 && (
        <ul className={cn('divide-y divide-[var(--hr-hairline)]', status === 'refreshing' && 'opacity-70 transition-opacity duration-150')} data-testid={`${testId}-list`}>
          {items.map((x) => {
            const code = codeOf(x);
            const inactive = isInactive(x);
            return (
              <li key={x.id}>
                <button type="button" onClick={() => openEdit(x)} className="group flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--hr-focus)] sm:px-5" data-testid={`${testId}-row-${x.id}`}>
                  {code && <span className="hr-tabular w-[72px] shrink-0 truncate text-[13px] text-[var(--hr-muted)]">{code}</span>}
                  <span className="min-w-0 flex-1">
                    <span className={cn('block truncate text-[14px] font-medium', inactive ? 'text-[var(--hr-muted)]' : 'text-[var(--hr-heading)]')}>{x[nameKey]}</span>
                    {metaOf(x).length > 0 && <span className="block truncate text-[13px] text-[var(--hr-muted)]">{metaOf(x).join(' · ')}</span>}
                  </span>
                  <span className="hidden shrink-0 text-[13px] text-[var(--hr-muted-2)] md:block" data-testid={`${testId}-usage-${x.id}`}>{usageText(x)}</span>
                  {x.unitLocked && <Lock className="hidden h-3.5 w-3.5 shrink-0 text-[var(--hr-muted-2)] md:block" aria-label="Enhet låst" />}
                  {isClosed(x) ? <StatusPill tone="neutral" data-testid={`${testId}-status-${x.id}`}>Avsluttet</StatusPill> : isArchived(x) ? <StatusPill tone="neutral" data-testid={`${testId}-status-${x.id}`}>Arkivert</StatusPill> : <StatusPill tone="green" data-testid={`${testId}-status-${x.id}`}>Aktiv</StatusPill>}
                  <Pencil className="hidden h-4 w-4 shrink-0 text-[var(--hr-muted-2)] opacity-0 transition-opacity duration-150 group-hover:opacity-100 sm:block" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <FormSheet
        open={!!sheet}
        onOpenChange={(o) => !o && setSheet(null)}
        title={sheet?.mode === 'ny' ? `Ny ${data?.singular.toLowerCase()}` : current ? `${current[nameKey]}` : ''}
        description={sheet?.mode === 'ny' ? 'Nødvendige felt først. Posten kan velges i registreringene straks den er lagret.' : current ? `${data?.idLabel ?? 'ID'} ${current.id} · ${usageText(current)}` : undefined}
        testId={`${testId}-sheet`}
        footer={
          sheet && (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                {editing && current && name === 'projects' && (
                  isClosed(current) ? (
                    <Button variant="outline" className="h-10 rounded-[10px] bg-white" onClick={() => setProjectStatus(current, 'aktiv')} disabled={saving} data-testid={`${testId}-reopen-button`}>
                      <ArchiveRestore className="mr-1.5 h-4 w-4" aria-hidden="true" /> Gjenåpne
                    </Button>
                  ) : (
                    <Button variant="outline" className="h-10 rounded-[10px] bg-white" onClick={() => setProjectStatus(current, 'avsluttet')} disabled={saving} data-testid={`${testId}-close-button`}>
                      <Archive className="mr-1.5 h-4 w-4" aria-hidden="true" /> Avslutt
                    </Button>
                  )
                )}
                {editing && current && name !== 'projects' && (
                  isArchived(current) ? (
                    <Button variant="outline" className="h-10 rounded-[10px] bg-white" onClick={() => setActive(current, true)} disabled={saving} data-testid={`${testId}-reactivate-button`}>
                      <ArchiveRestore className="mr-1.5 h-4 w-4" aria-hidden="true" /> Reaktiver
                    </Button>
                  ) : (
                    <Button variant="outline" className="h-10 rounded-[10px] bg-white" onClick={() => (current.openResponsibilities?.total ? setConfirm({ kind: 'arkiver', item: current }) : setActive(current, false))} disabled={saving} data-testid={`${testId}-archive-button`}>
                      <Archive className="mr-1.5 h-4 w-4" aria-hidden="true" /> Arkiver
                    </Button>
                  )
                )}
                {editing && current && !current.usage?.total && (
                  <Button variant="ghost" className="h-10 rounded-[10px] text-[var(--hr-muted)] hover:text-[var(--hr-red)]" onClick={() => setConfirm({ kind: 'slett', item: current })} disabled={saving} data-testid={`${testId}-delete-button`}>
                    <Trash2 className="mr-1.5 h-4 w-4" aria-hidden="true" /> Slett
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" className="h-10 rounded-[10px]" onClick={() => setSheet(null)} data-testid={`${testId}-cancel-button`}>Avbryt</Button>
                <Button className="h-10 rounded-[10px] bg-[var(--hr-heading)] px-4 font-semibold text-white shadow-none hover:bg-[var(--hr-navy)]" onClick={save} disabled={saving} data-testid={`${testId}-save-button`}>
                  {saving ? 'Lagrer …' : 'Lagre'}
                </Button>
              </div>
            </div>
          )
        }
      >
        {sheet && data && (
          <div className="space-y-4">
            {sheet.mode === 'ny' && data.idEditable && (
              <Field label={data.idLabel} htmlFor={`${name}-newid`} hint={data.nextId ? `Valgfritt – blir ${data.nextId} om det står tomt.` : 'Valgfritt – lages fra navnet om det står tomt.'}>
                <Input id={`${name}-newid`} value={form.newId} onChange={(e) => setForm((f) => ({ ...f, newId: e.target.value }))} maxLength={30} placeholder={data.nextId ?? ''} className={cn(inputClass, 'hr-tabular')} data-testid={`${testId}-newid`} />
              </Field>
            )}
            {Object.entries(fields).map(([key, spec]) => (
              <FieldInput key={key} name={name} fieldKey={key} spec={spec} form={form} setForm={setForm} errors={errors} refs={refs} item={current} testId={testId} />
            ))}
            {name === 'projects' && sheet.mode === 'ny' && (
              <Field label="Underprosjekter" htmlFor={`${name}-subs`} hint="Valgfritt – ett per linje. Kan også legges til senere.">
                <Textarea id={`${name}-subs`} value={form.subprojectsText} onChange={(e) => setForm((f) => ({ ...f, subprojectsText: e.target.value }))} rows={2} placeholder={'Grøft A – hovedledning\nGrøft B – stikkledninger'} className="min-h-[64px] rounded-[10px] border-[var(--hr-border)] text-[16px] shadow-none lg:text-[14px]" data-testid={`${testId}-subprojects-text`} />
              </Field>
            )}
            {name === 'projects' && editing && current && (
              <SheetSection title="Underprosjekter" description="Arkiverte underprosjekter tilbys ikke for nye registreringer, men står igjen på gamle.">
                <SubprojectEditor project={current} onChanged={replaceItem} testId={`${testId}-subprojects`} />
              </SheetSection>
            )}
            {name === 'employees' && editing && current?.openResponsibilities?.total > 0 && (
              <Note tone="neutral" className="text-[13px]" data-testid={`${testId}-responsibilities`}>
                Ansvarlig for {Object.entries(current.openResponsibilities.parts).map(([k, n]) => `${n} ${k}`).join(', ')}. Ved arkivering vises personen som «arkivert» der til noen andre tar over.
              </Note>
            )}
            {name === 'wageTypes' || name === 'supplements' ? (
              <p className="text-[13px] leading-5 text-[var(--hr-muted-2)]">Satser, overtidsregler og diett: <span className="font-medium text-[var(--hr-muted)]">Ikke konfigurert</span>. Dette er kun kategorisering for registrering og rapport.</p>
            ) : null}
            <SheetSection title={editing ? 'Årsak til endringen' : 'Merknad'} description="Valgfritt. Lagres i historikken sammen med før → etter.">
              <Input value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} maxLength={300} placeholder={editing ? 'F.eks. «Byttet avdeling»' : 'F.eks. «Ny i grøftelaget»'} className={inputClass} data-testid={`${testId}-reason`} />
            </SheetSection>
            {editing && current && (
              <SheetSection title="Historikk" description="Før → etter, merket «Kontor (eksempeldata)». Ikke en autentisert revisjonslogg.">
                <HistoryList history={current.history} testId={`${testId}-history`} />
              </SheetSection>
            )}
          </div>
        )}
      </FormSheet>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid={`${testId}-confirm-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.kind === 'slett' ? `Slette ${data?.singular.toLowerCase()}?` : `Arkivere ${confirm?.item?.name ?? ''}?`}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.kind === 'slett'
                ? 'Posten er ikke i bruk og fjernes helt. Poster som er i bruk kan bare arkiveres.'
                : `Personen er ansvarlig for ${Object.entries(confirm?.item?.openResponsibilities?.parts ?? {}).map(([k, n]) => `${n} ${k}`).join(', ')}. Arkivering tar personen ut av nye valg; eksisterende ansvar står igjen merket «arkivert» til noen andre tar over.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testId}-confirm-cancel`}>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={() => (confirm?.kind === 'slett' ? remove(confirm.item) : setActive(confirm.item, false))} data-testid={`${testId}-confirm-ok`}>
              {confirm?.kind === 'slett' ? 'Slett' : 'Arkiver'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Panel>
  );
}
